import { useEffect, useState, type FormEvent } from "react";
import { api, isMobileApp } from "../api";
import { useSession } from "./session";
import { ErrorNote } from "../ui";
import { logoUrl } from "../branding";
import "./login.css";

const DEMOS = [
  ["avery.chen@mblive.local", "Avery Chen", "Owner"],
  ["jordan.hale@mblive.local", "Jordan Hale", "Leader"],
  ["samir.qureshi@mblive.local", "Samir Qureshi", "Coordinator"],
  ["mina.raukawa@mblive.local", "Mina Raukawa", "Member"],
  ["theo.ngata@mblive.local", "Theo Ngata", "Volunteer"],
];

const POINTS = [
  "Service plans with teams and a weekly schedule.",
  "A music stand that opens the same set.",
  "People and the groups they belong to.",
];

const DEMO_NOTE = "Demo sign-in works only on this machine. The password also lives in the local access file, not in the page.";
const WORKSPACE_NOTE = "Sign in to your MB Live workspace.";

export function LoginPage() {
  const { refresh, loginMobile } = useSession();
  const [email, setEmail] = useState(isMobileApp() ? "" : "avery.chen@mblive.local");
  const [demoEnabled, setDemoEnabled] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isMobileApp()) return;
    api<{ demoEnabled: boolean }>("/api/v1/auth/config").then((config) => {
      setDemoEnabled(config.demoEnabled);
      if (!config.demoEnabled) setEmail("");
    }).catch(() => setDemoEnabled(false));
  }, []);

  async function enter(nextEmail: string, nextPassword?: string) {
    setError("");
    if (nextPassword !== undefined && nextPassword.length === 0) {
      setError("Enter your password.");
      return;
    }
    setSubmitting(true);
    try {
      if (isMobileApp()) {
        if (nextPassword === undefined) throw new Error("Enter your password.");
        await loginMobile(nextEmail, nextPassword);
      } else if (nextPassword !== undefined) {
        await api("/api/v1/auth/login", { method: "POST", body: JSON.stringify({ email: nextEmail, password: nextPassword }) });
      } else {
        await api("/api/v1/auth/demo", { method: "POST", body: JSON.stringify({ email: nextEmail }) });
      }
      await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Sign-in failed");
    } finally {
      setSubmitting(false);
    }
  }

  function signInWithPassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void enter(email, password);
  }

  return (
    <main className="mb-login">
      <div className="mb-login-canvas" aria-hidden="true">
        <span className="mb-login-orb mb-login-orb-violet" />
        <span className="mb-login-orb mb-login-orb-cyan" />
        <span className="mb-login-orb mb-login-orb-magenta" />
        <span className="mb-login-staff" />
      </div>
      <div className="mb-login-layout">
        <section className="mb-login-hero" aria-label="MB Live">
          <img className="mb-login-logo" src={logoUrl} alt="" />
          <p className="mb-login-kicker"><span className="mb-login-live" aria-hidden="true" /> {isMobileApp() ? "MB Music Stand" : "MB Live"}</p>
          <h1>{isMobileApp() ? "Your set starts here." : "Before the downbeat."}</h1>
          <p className="mb-login-lede">{isMobileApp() ? "Sign in to save your music and take it on stage." : "Plans, people, and a music stand for Moksha Base."}</p>
          <ul className="mb-login-points">
            {(isMobileApp() ? ["Service sets and songs in one place.", "Download charts before rehearsal.", "Keep the screen awake while you perform."] : POINTS).map((point) => <li key={point}>{point}</li>)}
          </ul>
        </section>
        <section className="mb-login-card" aria-labelledby="mb-login-title">
          <h2 id="mb-login-title">Enter the room</h2>
          <p className="mb-login-note">{demoEnabled ? DEMO_NOTE : WORKSPACE_NOTE}</p>
          <div id="mb-login-error">{error ? <ErrorNote>{error}</ErrorNote> : null}</div>
          <form className="mb-login-form" onSubmit={signInWithPassword} aria-busy={submitting}>
            <div className="mb-login-field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="username"
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? "mb-login-error" : undefined}
                disabled={submitting}
              />
            </div>
            <div className="mb-login-field">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? "mb-login-error" : undefined}
                disabled={submitting}
              />
            </div>
            <button className="mb-login-submit" type="submit" disabled={submitting}>
              {submitting ? "Signing in…" : "Sign in with password"}
            </button>
          </form>
          {demoEnabled && (
            <div className="mb-login-demos">
              <p className="mb-login-demos-label" id="mb-login-demos-label">Local demo accounts</p>
              {DEMOS.map(([address, name, role]) => (
                <button
                  className="mb-login-demo"
                  type="button"
                  key={address}
                  disabled={submitting}
                  aria-describedby="mb-login-demos-label"
                  onClick={() => { void enter(address); }}
                >
                  {name} · {role}
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
